import React, { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import {
  useAppUpdates,
  loadAppUpdates,
  markAppUpdatesRead,
  resetAppUpdates,
} from '@/lib/appUpdatesStore';

/**
 * Popup "Apa yang Baru". Muncul sekali per update per user (status baca
 * disimpan di server, jadi berlaku di semua perangkat). Dipasang sekali di
 * App.js. Tidak ada polling: daftar diambil satu kali saat user login.
 */
export default function AppUpdatesPopup({ user }) {
  const { items } = useAppUpdates();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) {
      resetAppUpdates();
      setDismissed(false);
      return undefined;
    }
    const t = setTimeout(() => loadAppUpdates(), 300);
    return () => clearTimeout(t);
  }, [user]);

  if (!user || dismissed || items.length === 0) return null;

  const close = () => {
    setDismissed(true);
    markAppUpdatesRead(items.map((i) => i.id));
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Apa yang Baru"
      data-testid="app-updates-popup"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <Sparkles className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900 flex-1">Apa yang Baru</h2>
          <button
            type="button"
            onClick={close}
            aria-label="Tutup"
            className="p-1 rounded-full hover:bg-black/5"
            data-testid="app-updates-popup-close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {items.map((it) => (
            <div key={it.id}>
              <p className="font-semibold text-gray-900">{it.title}</p>
              <ul className="mt-1.5 space-y-1">
                {(it.points || []).map((p, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex gap-2">
                    <span className="text-blue-500">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t">
          <button
            type="button"
            onClick={close}
            className="w-full rounded-lg bg-blue-600 text-white py-2.5 text-sm font-medium hover:bg-blue-700"
            data-testid="app-updates-popup-ok"
          >
            Mengerti
          </button>
        </div>
      </div>
    </div>
  );
}
